import { useState, useMemo, useEffect } from "react";
import { useFeedback } from "./hooks/useFeedback";
import { useUpdateFeedback } from "./hooks/useUpdateFeedback";
import { useDeleteFeedback } from "./hooks/useDeleteFeedback";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { LoadingState } from "@/components/ui/loading-state";
import { EmptyState } from "@/components/ui/empty-state";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Bug,
  Lightbulb,
  Building2,
  Mail,
  Calendar,
  Paperclip,
  MessageSquare,
  Search,
  ExternalLink,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/utils/shadcn";
import type { FeedbackType, FeedbackStatus, FeedbackPriority } from "@/models/feedback";
import { InlineSelect, InlineSelectOption } from "./components/InlineSelect";
import {
  PRIORITY_BADGE_VARIANT,
  PRIORITY_LABELS,
  PRIORITY_ORDER,
  STATUS_BADGE_VARIANT,
  STATUS_LABELS,
  STATUS_ORDER
} from "./feedback.meta";

type TypeFilter = "all" | FeedbackType;
type StatusFilter = "all" | FeedbackStatus;

const STATUS_OPTIONS: InlineSelectOption<FeedbackStatus>[] = STATUS_ORDER.map((value) => ({
  value,
  label: STATUS_LABELS[value],
  variant: STATUS_BADGE_VARIANT[value]
}));

const PRIORITY_OPTIONS: InlineSelectOption<FeedbackPriority>[] = PRIORITY_ORDER.map((value) => ({
  value,
  label: PRIORITY_LABELS[value],
  variant: PRIORITY_BADGE_VARIANT[value]
}));

const FeedbackPage = () => {
  const { data: feedbackItems, isLoading, error, refetch } = useFeedback();
  const updateFeedback = useUpdateFeedback();
  const deleteFeedback = useDeleteFeedback();

  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [noteDraft, setNoteDraft] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const selected = selectedId
    ? (feedbackItems?.find((item) => item._id === selectedId) ?? null)
    : null;

  // Reset the note draft / delete confirm whenever a different item is opened.
  useEffect(() => {
    setNoteDraft(selected?.internalNote ?? "");
    setConfirmingDelete(false);
  }, [selectedId]);

  const filtered = useMemo(() => {
    if (!feedbackItems) return [];
    return feedbackItems.filter((item) => {
      if (typeFilter !== "all" && item.type !== typeFilter) return false;
      if (statusFilter !== "all" && (item.status ?? "new") !== statusFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          item.description.toLowerCase().includes(q) ||
          item.organizationId.name.toLowerCase().includes(q) ||
          item.submittedBy.name.toLowerCase().includes(q) ||
          item.page.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [feedbackItems, typeFilter, statusFilter, search]);

  const handleSaveNote = () => {
    if (!selected) return;
    updateFeedback.mutate({ id: selected._id, payload: { internalNote: noteDraft.trim() } });
  };

  const handleDelete = () => {
    if (!selected) return;
    deleteFeedback.mutate(selected._id, { onSuccess: () => setSelectedId(null) });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feedback"
        description="Bug reports and feature requests submitted by users"
      />

      {isLoading ? (
        <LoadingState isLoading={isLoading} loadingText="Loading feedback..." />
      ) : error ? (
        <LoadingState
          error={error}
          errorTitle="Failed to load feedback"
          errorDescription="There was an error loading the feedback. Please try again."
          onRetry={() => refetch()}
        />
      ) : !feedbackItems || feedbackItems.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No feedback yet"
          description="Feedback submitted from the dashboard will appear here."
        />
      ) : (
        <>
          {/* Filter bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center rounded-lg border p-1 gap-1">
              {(["all", "bug", "feature"] as TypeFilter[]).map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={typeFilter === t ? "default" : "ghost"}
                  className="h-7 px-3 text-xs capitalize"
                  onClick={() => setTypeFilter(t)}
                >
                  {t === "bug" && <Bug className="h-3 w-3 mr-1" />}
                  {t === "feature" && <Lightbulb className="h-3 w-3 mr-1" />}
                  {t === "all" ? "All" : t === "bug" ? "Bug" : "Feature"}
                </Button>
              ))}
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger className="h-9 w-[150px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_ORDER.map((value) => (
                  <SelectItem key={value} value={value}>
                    {STATUS_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search description, org, user, page..."
                className="pl-8 h-9 text-sm"
              />
            </div>
            {(typeFilter !== "all" || statusFilter !== "all" || search) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-muted-foreground"
                onClick={() => {
                  setTypeFilter("all");
                  setStatusFilter("all");
                  setSearch("");
                }}
              >
                Clear filters
              </Button>
            )}
            <span className="text-xs text-muted-foreground ml-auto">
              {filtered.length} of {feedbackItems.length}
            </span>
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No results"
              description="Try adjusting your filters or search query."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Page</TableHead>
                      <TableHead>Submitted By</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((item) => {
                      const status = item.status ?? "new";
                      const priority = item.priority ?? "medium";
                      const isClosed = status === "closed";
                      return (
                        <TableRow
                          key={item._id}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50 transition-colors",
                            isClosed && "bg-muted/40 opacity-70 hover:bg-muted/60"
                          )}
                          onClick={() => setSelectedId(item._id)}
                        >
                          <TableCell>
                            {item.type === "bug" ? (
                              <Badge variant="destructive" className="gap-1 whitespace-nowrap">
                                <Bug className="h-3 w-3" />
                                Bug
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="gap-1 whitespace-nowrap">
                                <Lightbulb className="h-3 w-3" />
                                Feature
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-start gap-2">
                              <div className="p-1.5 rounded-lg bg-primary/10 border border-primary/20 shrink-0">
                                <Building2 className="h-3.5 w-3.5 text-primary" />
                              </div>
                              <div>
                                <div className="font-medium text-foreground text-sm">
                                  {item.organizationId.name}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground font-mono">
                              {item.page}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">{item.submittedBy.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {item.submittedBy.email}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[260px]">
                            <p
                              className={cn(
                                "text-sm text-muted-foreground line-clamp-2",
                                isClosed && "line-through"
                              )}
                            >
                              {item.description}
                            </p>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <InlineSelect
                              value={priority}
                              options={PRIORITY_OPTIONS}
                              onChange={(value) =>
                                updateFeedback.mutate({
                                  id: item._id,
                                  payload: { priority: value }
                                })
                              }
                              disabled={updateFeedback.isPending}
                              ariaLabel="Change priority"
                            />
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <InlineSelect
                              value={status}
                              options={STATUS_OPTIONS}
                              onChange={(value) =>
                                updateFeedback.mutate({
                                  id: item._id,
                                  payload: { status: value }
                                })
                              }
                              disabled={updateFeedback.isPending}
                              ariaLabel="Change status"
                            />
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5" />
                              {format(new Date(item.createdAt), "MMM d, yyyy")}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent side="right" className="sm:max-w-md w-full overflow-y-auto flex flex-col">
          {selected && (
            <>
              <SheetHeader className="pb-4">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  {selected.type === "bug" ? (
                    <Badge variant="destructive" className="gap-1">
                      <Bug className="h-3 w-3" />
                      Bug Report
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1">
                      <Lightbulb className="h-3 w-3" />
                      Feature Request
                    </Badge>
                  )}
                  <Badge variant={STATUS_BADGE_VARIANT[selected.status ?? "new"]}>
                    {STATUS_LABELS[selected.status ?? "new"]}
                  </Badge>
                  <Badge variant={PRIORITY_BADGE_VARIANT[selected.priority ?? "medium"]}>
                    {PRIORITY_LABELS[selected.priority ?? "medium"]}
                  </Badge>
                </div>
                <SheetTitle className="text-base leading-snug">
                  {selected.type === "bug" ? "Bug Report" : "Feature Request"}
                </SheetTitle>
              </SheetHeader>

              <Separator />

              <div className="mt-5 space-y-5">
                {/* Description */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Description
                  </p>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                    {selected.description}
                  </p>
                </div>

                <Separator />

                {/* Metadata grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Organization
                    </p>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium">{selected.organizationId.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 pl-5">
                      {selected.organizationId._id}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Submitted By
                    </p>
                    <div className="text-sm font-medium">{selected.submittedBy.name}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                      <Mail className="h-3 w-3" />
                      {selected.submittedBy.email}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Page
                    </p>
                    <span className="text-sm font-mono text-muted-foreground">{selected.page}</span>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                      Date
                    </p>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" />
                      {format(new Date(selected.createdAt), "MMM d, yyyy · h:mm a")}
                    </div>
                  </div>
                </div>

                {/* Attachment */}
                {selected.attachmentUrl && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Attachment
                      </p>
                      <a
                        href={selected.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        View attachment
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </>
                )}

                <Separator />

                {/* Internal note */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Internal note
                  </p>
                  <Textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Admin-only note (e.g. reproduced on staging, duplicate of #123)…"
                    className="min-h-[90px] text-sm"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      onClick={handleSaveNote}
                      disabled={
                        updateFeedback.isPending ||
                        noteDraft.trim() === (selected.internalNote ?? "")
                      }
                    >
                      {updateFeedback.isPending ? "Saving..." : "Save note"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Danger zone */}
              <div className="mt-auto border-t pt-4">
                {confirmingDelete ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      This permanently deletes the feedback and its attachment. This can&apos;t be
                      undone.
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmingDelete(false)}
                        disabled={deleteFeedback.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDelete}
                        disabled={deleteFeedback.isPending}
                      >
                        {deleteFeedback.isPending ? "Deleting..." : "Delete permanently"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => setConfirmingDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete feedback
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default FeedbackPage;
